// SPDX-License-Identifier: AGPL-3.0-or-later
import type { TrafficLight } from "@almanac/domain";
import type { PeriodStatus } from "../lib/figures.js";
import { useI18n } from "../lib/i18n.js";

const PERIOD_KEY = { day: "status.day", week: "status.week", month: "status.month", year: "period.year" } as const;
const LIGHT_KEY = { green: "status.green", amber: "status.amber", red: "status.red" } as const;

/** Each status has its own shape as well as colour, so it reads without colour vision. */
function Icon({ light }: { light: TrafficLight }) {
  const fill = light === "green" ? "var(--positive)" : light === "amber" ? "var(--amber)" : "var(--negative)";
  return (
    <svg className="light-icon" viewBox="0 0 30 30" aria-hidden="true" focusable="false">
      {light === "green" && (
        <>
          <circle cx="15" cy="15" r="13" fill={fill} />
          <path d="M9 15.5l4 4 8-9" stroke="#fff" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}
      {light === "amber" && (
        <>
          <path d="M15 2 L28 27 H2 Z" fill={fill} />
          <rect x="13.5" y="10" width="3" height="9" rx="1.5" fill="#fff" />
          <circle cx="15" cy="22.5" r="1.8" fill="#fff" />
        </>
      )}
      {light === "red" && (
        <>
          <path d="M9 2h12l7 7v12l-7 7H9l-7-7V9z" fill={fill} />
          <path d="M10 10l10 10M20 10L10 20" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}

export function TrafficLights({ statuses }: { statuses: PeriodStatus[] }) {
  const { t, money } = useI18n();
  return (
    <ul className="lights">
      {statuses.map((s) => (
        <li className="light" key={s.period}>
          <Icon light={s.light} />
          <div>
            <strong>
              {t(PERIOD_KEY[s.period])}: {t(LIGHT_KEY[s.light])}
            </strong>
            <span>{t("status.detail", { spent: money(s.spent), paced: money(s.paced) })}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}
